import { useState } from 'react';
import PasswordStrengthBar from 'react-password-strength-bar';
import { Link } from 'react-router-dom';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import {
  getAccountCreationSettings,
  getParamFromUrl,
  trackError,
} from 'utils/helpers';
import { fetchReCapchaToken } from 'components/checkout_form/gift_card/helpers';
import useGoogleRecaptcha from 'components/checkout_form/gift_card/use_google_recaptcha';
import PasswordField from 'components/formik/password_field/password_field';
import LoadingButton from 'components/loading_button';
import { useCreateAccountMutation } from 'redux/api_slice/account';
import { useDocumentTitle } from 'tenants/sealink/utils/use_document_title';
import { formSchema } from './helpers';

function CreateAccount() {
  useDocumentTitle('Create Account');
  useGoogleRecaptcha();

  const { requireTitle, titles } = getAccountCreationSettings();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [wasSuccessful, setWasSuccessful] = useState(false);
  const [serverMessage, setServerMessage] = useState(null);
  const [alertStyle, setAlertStyle] = useState('alert-danger');
  const [createAccount] = useCreateAccountMutation();
  const onSubmit = async (values) => {
    setIsSubmitting(true);
    setServerMessage(null);

    try {
      const recaptchaToken = await fetchReCapchaToken();
      const { error } = await createAccount({
        ...values,
        'g-recaptcha-response': recaptchaToken,
      });

      if (error) {
        if (error?.data?.message) {
          throw new Error(error.data.message);
        }

        throw new Error('An error occurred');
      }

      setWasSuccessful(true);
      setAlertStyle('alert-success');
      setServerMessage(
        'Please check your email for an account verification link.'
      );
    } catch (error) {
      trackError(error);
      setIsSubmitting(false);
      setServerMessage(error.message);
    }
  };

  const queryParams = getParamFromUrl();
  const initialValues = {
    firstName: queryParams.firstName || '',
    lastName: queryParams.lastName || '',
    email: queryParams.email || '',
    password: '',
  };

  if (requireTitle) {
    initialValues.title = titles[0];
  }

  return (
    <div className="booking-login d-flex flex-column gap-3">
      <h3>Create account</h3>
      {serverMessage && (
        <div className={`alert ${alertStyle} alert-dismissible fade show`}>
          {serverMessage}
          <button
            type="button"
            className="btn-close"
            data-testid="dismiss-message"
            onClick={() => setServerMessage(null)}
          />
        </div>
      )}
      {!wasSuccessful && (
        <Formik
          validationSchema={formSchema}
          initialValues={initialValues}
          onSubmit={onSubmit}
        >
          {({ values }) => (
            <Form className="d-flex flex-column gap-3">
              {requireTitle ? (
                <div>
                  <label htmlFor="title" className="form-label">
                    Title
                  </label>
                  <Field
                    as="select"
                    name="title"
                    className="form-select"
                    id="title"
                  >
                    {titles.map((title) => (
                      <option key={title} value={title}>
                        {title}
                      </option>
                    ))}
                  </Field>
                </div>
              ) : null}
              <div>
                <label htmlFor="firstName" className="form-label">
                  First Name
                  <span className="booking-login__hint">(required)</span>
                </label>
                <Field
                  name="firstName"
                  className="form-control"
                  id="firstName"
                  placeholder="First Name"
                />
                <ErrorMessage
                  className="text-danger text-small mt-1"
                  name="firstName"
                  component="div"
                />
              </div>
              <div>
                <label htmlFor="lastName" className="form-label">
                  Last Name
                  <span className="booking-login__hint">(required)</span>
                </label>
                <Field
                  name="lastName"
                  className="form-control"
                  id="lastName"
                  placeholder="Last Name"
                />
                <ErrorMessage
                  className="text-danger text-small mt-1"
                  name="lastName"
                  component="div"
                />
              </div>
              <div>
                <label htmlFor="email" className="form-label">
                  Email Address
                  <span className="booking-login__hint">(required)</span>
                </label>
                <Field
                  name="email"
                  className="form-control"
                  id="email"
                  placeholder="Email Address"
                />
                <ErrorMessage
                  className="text-danger text-small mt-1"
                  name="email"
                  component="div"
                />
              </div>
              <div>
                <label htmlFor="password" className="form-label">
                  Password
                  <span className="booking-login__hint">(required)</span>
                </label>
                <PasswordField
                  name="password"
                  id="password"
                  placeholder="Password"
                />
                <ErrorMessage
                  className="text-danger text-small mt-1"
                  name="password"
                  component="div"
                />
              </div>
              {values.password.length > 9 && (
                <PasswordStrengthBar password={values.password} />
              )}
              <LoadingButton
                type="submit"
                disabled={isSubmitting}
                className="btn btn-primary w-100"
                loading={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Create Account'}
              </LoadingButton>
              <div className="text-small">
                Already have an account? <Link to="/account/login">Login</Link>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </div>
  );
}

export default CreateAccount;
